import { AudioLines, Check, Folder, ImageIcon, Play, Video } from "lucide-react"
import { type MouseEvent, type ReactNode } from "react"
import { FrameStatusTile } from "@/components/media/frame-status-tile"
import { libraryItemDurationMs } from "@/lib/library-sort"
import type { LibraryPickMedia } from "@/lib/library-source"
import { UI_CHOICE_CONTROL, UI_CHOICE_CONTROL_LG } from "@/lib/ui-selection"
import { cn } from "@/lib/utils"
import type { ShotFrameSource } from "@/lib/workspace-api"

export type LibraryItemKind = "folder" | "image" | "video" | "audio"

/** Ассет, открытый в лайтбоксе: видео идёт в плеер с таймлайном, картинка и аудио — нативно. */
export type LibraryAsset = {
  title: string
  kind: "image" | "video" | "audio"
  /** Полноразмерная картинка или аудиофайл. */
  url?: string | null
  /** Видео: same-origin поток (stream_url), иначе плеер не сможет читать кадры. */
  videoUrl?: string | null
  downloadUrl?: string | null
  createdAt?: string | null
  /** Длительность медиа (файла-источника): по ней движок ограничивает перемотку. */
  durationMs?: number | null
  frameRate?: number | null
  aspectRatio?: number | null
  timelineFrameUrls?: string[]
  /** Идентификатор источника для движка плеера: id версии либо синтетический. */
  sourceId?: number
  /** Подрезка внутри источника (сегмент аниматика): играем только этот диапазон. */
  sourceInMs?: number | null
  sourceOutMs?: number | null
}

/**
 * Единица содержимого папки Библиотеки: и вложенная папка, и любой ассет
 * (кадр, скриншот, видео-версия, генерация Холста, референс) — один тип и
 * один компонент на оба режима просмотра.
 */
export type LibraryItem = {
  key: string
  kind: LibraryItemKind
  label: string
  /** Хвост подписи через точку: таймкод, число вложений, длительность. */
  meta?: string | null
  /** ISO-дата создания — колонка «Дата» в режиме списка. */
  createdAt?: string | null
  thumbUrl?: string | null
  /** Асинхронный статус (скриншоты): не-ready даёт спиннер или плейсхолдер вместо картинки. */
  status?: string
  errorTitle?: string | null
  /** Ассет открывается в лайтбоксе; папка вместо этого переходит внутрь через onOpen. */
  asset?: LibraryAsset
  /**
   * Чем этот ассет является для сервера: позволяет назначить его опорником, раскадровкой
   * или референсом. Отсутствует у папок и у legacy-превью, за которыми нет файла.
   */
  source?: ShotFrameSource
  /**
   * Тип для назначения, когда он не совпадает с kind: кусок ленты, шотовый ролик, превиз —
   * их берут только вкладки «Аниматик»/«Превиз».
   */
  pickMedia?: LibraryPickMedia
  lockedReason?: string | null
  /**
   * Ассет активной версии шота: зелёная галочка справа от названия,
   * и в таблице, и на плитке.
   */
  current?: boolean
  /** Своя пропорция плитки. У звука нет кадра — низкая плитка, а не формат проекта. */
  aspectClass?: string
  onOpen?: () => void
}

/** Атрибут, по которому клавиатурная навигация находит элементы в DOM-порядке. */
export const LIBRARY_ITEM_ATTR = "data-library-item"

/**
 * Кнопка просмотра плитки лежит РЯДОМ с кнопкой элемента, а не внутри неё — иначе получилась
 * бы вложенная кнопка. Из-за этого «клик мимо элемента» принимал её за пустое место и
 * сбрасывал курсор. Помечаем её отдельно, чтобы курсор оставался на месте.
 */
export const LIBRARY_ITEM_ACTION_ATTR = "data-library-item-action"

function itemCaption(item: LibraryItem): string {
  return item.meta ? `${item.label} · ${item.meta}` : item.label
}

/** Имя бывшей папки перед подписью. Совпадает с подписью — не повторяем. */
export function prefixLibraryLabel(prefix: string | null | undefined, label: string): string {
  const folder = prefix?.trim() ?? ""
  if (!folder) return label
  if (folder.localeCompare(label, undefined, { sensitivity: "accent" }) === 0) return label
  return `${folder} - ${label}`
}

/**
 * Хронометраж видео — из ассета, а не из подписи: у кадров его нет, у видео он есть всегда.
 * Без сотых, в отличие от таймкода скриншота: тут важна длина, а не точка на шкале.
 * У подрезанного клипа (сегмент аниматика) длина — его диапазон, а не весь файл-источник.
 */
function itemDuration(item: LibraryItem): string | null {
  const ms = libraryItemDurationMs(item)
  if (ms == null) return null
  const total = Math.round(ms / 1000)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
}

export type LibraryKindCounts = Record<LibraryItemKind, number>

/** Сколько видео, картинок и аудио в списке — счётчики фильтра. */
export function countLibraryKinds(items: LibraryItem[]): LibraryKindCounts {
  const counts: LibraryKindCounts = { folder: 0, image: 0, video: 0, audio: 0 }
  for (const item of items) counts[item.kind] += 1
  return counts
}

export const LIBRARY_KIND_ICON: Record<LibraryItemKind, typeof Video> = {
  folder: Folder,
  image: ImageIcon,
  video: Video,
  audio: AudioLines,
}

function formatLibraryPart(
  iso: string | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions,
  fallback: string,
): string {
  if (!iso) return fallback
  try {
    return new Date(iso).toLocaleString(locale, options)
  } catch {
    return fallback
  }
}

/** Короткая дата ассета: «12 авг, 14:30» в текущей локали. Одна ячейка и у групп, и в таблице. */
export function formatLibraryDate(iso: string | null | undefined, locale: string, fallback = "—"): string {
  return formatLibraryPart(
    iso,
    locale,
    { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" },
    fallback,
  )
}

/** Зелёная галочка активной версии. Стоит справа от названия, отдельной колонки нет. */
function CurrentVersionMark({ label }: { label: string }) {
  return (
    <span title={label} className="inline-flex shrink-0 items-center justify-center text-[var(--ui-badge-emerald-fg)]">
      <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  )
}

export function ItemMedia({
  item,
  className,
  flush = false,
  compact = false,
}: {
  item: LibraryItem
  className?: string
  /** Превью лежит внутри карточки со своей рамкой — гасим собственную, иначе рамка двойная. */
  flush?: boolean
  /** Миниатюра строки таблицы: кружок Play соразмерен кадру, а не закрывает его. */
  compact?: boolean
}) {
  const showFolderIcon = item.kind === "folder" && !item.thumbUrl
  const showAudioIcon = item.kind === "audio" && !item.thumbUrl
  return (
    <span className={cn("relative block overflow-hidden", className)}>
      <FrameStatusTile
        status={item.status ?? "ready"}
        url={item.thumbUrl ?? null}
        alt={item.label}
        errorTitle={item.errorTitle}
        fallbackLabel={showFolderIcon || showAudioIcon ? "" : "—"}
        className={cn("h-full w-full", flush && "rounded-none border-0")}
      />
      {showFolderIcon ? (
        <span className="absolute inset-0 flex items-center justify-center text-ui-text-muted">
          <Folder className="h-5 w-5" aria-hidden />
        </span>
      ) : null}
      {showAudioIcon ? (
        <span className="absolute inset-0 flex items-center justify-center text-ui-text-muted">
          <AudioLines className="h-5 w-5" aria-hidden />
        </span>
      ) : null}
      {item.kind === "video" ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <span
            className={cn(
              "flex items-center justify-center rounded-full bg-[var(--ui-chip-bg-strong)] text-ui-text-primary ring-1 ring-ui-border backdrop-blur-sm",
              compact ? "h-3.5 w-3.5" : "h-7 w-7",
            )}
          >
            <Play className={cn("translate-x-px", compact ? "h-2 w-2" : "h-3 w-3")} fill="currentColor" />
          </span>
        </span>
      ) : null}
    </span>
  )
}

/**
 * Режим выбора: клик открывает ассет, чекбокс отмечает его для назначения.
 * Невыбираемые элементы не прячем — гасим и объясняем причину,
 * иначе пользователь ищет пропавший кадр.
 */
export type LibraryPickProps = {
  picking?: boolean
  /** Переведённая причина запрета; null — элемент выбираем. */
  unpickable?: string | null
  /** Ассет отмечен чекбоксом. Кольцо курсора от этого не зависит. */
  selected?: boolean
  /** shiftKey — диапазон в мультивыборе. Без модификатора чекбокс переключает один ассет. */
  onToggle?: (shiftKey: boolean) => void
  toggleLabel?: string
}

type OpenButtonProps = {
  item: LibraryItem
  focused: boolean
  /** false — обводку рисует внешний контейнер (в списке она охватывает всю строку). */
  ringed?: boolean
  onOpen: () => void
  onFocus: () => void
  className: string
  title?: string
  children: React.ReactNode
}

/** Кнопка элемента с roving tabindex: фокус ведёт клавиатура, клик синхронизирует курсор. */
function ItemOpenButton({
  item,
  focused,
  ringed = true,
  onOpen,
  onFocus,
  className,
  title,
  children,
}: OpenButtonProps) {
  return (
    <button
      type="button"
      {...{ [LIBRARY_ITEM_ATTR]: item.key }}
      tabIndex={focused ? 0 : -1}
      onClick={(e) => {
        onFocus()
        // Двойной клик даёт два click подряд. Второй не должен открывать просмотрщик ещё раз.
        // detail === 0 у клавиатурного Enter.
        if (e.detail > 1) return
        onOpen()
      }}
      title={title}
      className={cn(className, "outline-none", focused && ringed && "ring-2 ring-ui-accent ring-inset")}
    >
      {children}
    </button>
  )
}

/** Один click. shiftKey читаем здесь: label и второй жест браузера отметку не повторяют. */
function onPickClick(event: MouseEvent, onToggle: (shiftKey: boolean) => void, disabled: boolean) {
  event.preventDefault()
  event.stopPropagation()
  if (disabled) return
  onToggle(event.shiftKey)
}

/**
 * Чекбокс плитки — тот же choice-control, что в таблице, только крупнее.
 * Хитбокс — угол плитки. Клик отмечает, не открывает.
 */
function PickCheckbox({
  selected,
  disabled,
  label,
  onToggle,
}: {
  selected: boolean
  disabled: boolean
  label: string
  onToggle: (shiftKey: boolean) => void
}) {
  return (
    <button
      type="button"
      {...{ [LIBRARY_ITEM_ACTION_ATTR]: "" }}
      tabIndex={-1}
      aria-pressed={selected}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(event) => onPickClick(event, onToggle, disabled)}
      className={cn(
        "absolute top-0 right-0 z-10 flex h-10 w-10 items-start justify-end p-1.5 outline-none",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
      )}
    >
      {/* Глиф только рисует состояние. Клик ловит кнопка, иначе отметка срабатывает дважды. */}
      <input
        type="checkbox"
        tabIndex={-1}
        checked={selected}
        disabled={disabled}
        aria-hidden
        className={cn(UI_CHOICE_CONTROL_LG, "pointer-events-none")}
        onChange={() => undefined}
      />
    </button>
  )
}

/**
 * Чекбокс строки списка — обычный choice-control. Колонка узкая, хитбокс на всю её высоту
 * и чуть шире глифа: левый отступ строки и промежуток до названия тоже отмечают ассет.
 */
function ListPickCheckbox({
  selected,
  disabled,
  label,
  onToggle,
}: {
  selected: boolean
  disabled: boolean
  label: string
  onToggle: (shiftKey: boolean) => void
}) {
  return (
    <span
      {...{ [LIBRARY_ITEM_ACTION_ATTR]: "" }}
      title={label}
      onClick={(event) => onPickClick(event, onToggle, disabled)}
      className={cn(
        "relative flex h-full w-full items-center justify-center self-stretch",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
        // Ячейка и так на высоту строки. Псевдоэлемент захватывает мёртвые зоны вокруг неё.
        "before:absolute before:inset-y-0 before:-right-3 before:-left-2 before:content-['']",
      )}
    >
      {/* Не label: клик по label браузер повторяет на input, и отмечается чужая строка. */}
      <input
        type="checkbox"
        tabIndex={-1}
        checked={selected}
        disabled={disabled}
        aria-label={label}
        className={cn(UI_CHOICE_CONTROL, "pointer-events-none relative")}
        onChange={() => undefined}
      />
    </span>
  )
}

export function LibraryItemTile({
  item,
  aspectClass,
  focused,
  onOpen,
  onFocus,
  actions,
  labelAction,
  picking = false,
  unpickable = null,
  selected = false,
  onToggle,
  toggleLabel = "",
  currentLabel,
}: {
  item: LibraryItem
  aspectClass: string
  focused: boolean
  onOpen: () => void
  onFocus: () => void
  /** Действия поверх превью (удалить). В режиме выбора угол занят чекбоксом. */
  actions?: ReactNode
  /**
   * Действие на строке подписи (переименовать). Поверх превью карандаш читался бы как
   * правка изображения — этой иконкой в проекте открывают эскиз.
   */
  labelAction?: ReactNode
  /** Подпись галочки активной версии. Без неё отметка не рисуется. */
  currentLabel?: string
} & LibraryPickProps) {
  const caption = itemCaption(item)
  const dimmed = picking && unpickable != null
  const duration = itemDuration(item)
  // В режиме выбора подпись отдана только тексту: там действий над элементом нет.
  const showLabelAction = !picking && labelAction != null
  return (
    <div
      className={cn(
        "relative min-w-0 overflow-hidden rounded-lg border bg-ui-surface",
        picking && "select-none",
        picking && selected ? "border-ui-accent" : "border-ui-border hover:border-ui-border-strong",
        // Курсор — кольцо на самой карточке и СНАРУЖИ рамки. Внутреннее кольцо тут не годится
        // дважды: в слое фона его закрывает непрозрачное превью, а сдвинутый внутрь слой
        // расходится с карточкой по радиусу скругления, и в углах появляется щель.
        focused && "ring-2 ring-ui-accent",
        dimmed && "opacity-50",
      )}
    >
      <ItemOpenButton
        item={item}
        focused={focused}
        ringed={false}
        onOpen={onOpen}
        onFocus={onFocus}
        title={unpickable ?? caption}
        className="block w-full min-w-0 text-left"
      >
        <span className="relative block">
          <ItemMedia item={item} flush className={cn("w-full", aspectClass)} />
          {/* Хронометраж — поверх кадра, а не в подписи: подпись и так занята названием. */}
          {duration ? (
            <span className="absolute right-1.5 bottom-1.5 rounded bg-[var(--ui-chip-bg-strong)] px-1.5 py-0.5 text-[10px] text-ui-text-primary tabular-nums backdrop-blur-sm">
              {duration}
            </span>
          ) : null}
        </span>
        <span className="flex h-7 items-center px-2">
          <span className="flex min-w-0 flex-1 items-baseline gap-2">
            <span className="min-w-0">
              <span className="block truncate text-[11px] text-ui-text-secondary">{item.label}</span>
            </span>
            {item.current && currentLabel ? (
              <span className="inline-flex self-center">
                <CurrentVersionMark label={currentLabel} />
              </span>
            ) : null}
            {/* Таймкод и хронометраж коротки и должны читаться целиком, а имя файла у ассета
                сущности бывает длинным — ему разрешаем урезаться, но не больше половины строки.
                Обрезка на внутреннем span: overflow у самого flex-элемента сбивает базовую линию. */}
            {item.meta ? (
              <span className="max-w-[55%] shrink-0">
                <span className="block truncate text-[10px] text-ui-text-muted tabular-nums">{item.meta}</span>
              </span>
            ) : null}
          </span>
          {/* Распорка под кнопку подписи: сама кнопка лежит СНАРУЖИ кнопки элемента —
              вложенная кнопка невалидна, — и без места название уезжало бы под неё. */}
          {showLabelAction ? <span className="w-4 shrink-0" aria-hidden /> : null}
        </span>
      </ItemOpenButton>
      {picking && item.kind !== "folder" ? (
        <PickCheckbox
          selected={selected}
          disabled={unpickable != null && !selected}
          label={toggleLabel}
          onToggle={onToggle ?? (() => undefined)}
        />
      ) : null}
      {!picking && actions ? <span className="absolute top-1.5 right-1.5 flex gap-1">{actions}</span> : null}
      {showLabelAction ? (
        <span className="absolute right-1.5 bottom-0 flex h-7 items-center">{labelAction}</span>
      ) : null}
    </div>
  )
}

export function LibraryItemRow({
  item,
  typeLabel,
  dateLabel,
  rowClass,
  layout = "compact",
  currentLabel,
  focused,
  onOpen,
  onFocus,
  actions,
  picking = false,
  unpickable = null,
  selected = false,
  onToggle,
  toggleLabel = "",
}: {
  item: LibraryItem
  typeLabel: string
  /** Дата и время одной строкой: «08 окт., 18:51». */
  dateLabel: string
  rowClass: string
  /** compact — прежние строки без шапки. table — все колонки видны и на узкой ширине. */
  layout?: "compact" | "table"
  currentLabel?: string
  focused: boolean
  onOpen: () => void
  onFocus: () => void
  actions?: ReactNode
} & LibraryPickProps) {
  const dimmed = picking && unpickable != null
  const duration = itemDuration(item)
  const KindIcon = LIBRARY_KIND_ICON[item.kind]
  const metricClass =
    layout === "table"
      ? "text-right text-xs text-ui-text-muted tabular-nums"
      : "hidden text-right text-xs text-ui-text-muted tabular-nums sm:block"
  return (
    <div
      className={cn(
        rowClass,
        picking && "select-none",
        selected && "bg-ui-accent/10 hover:bg-ui-accent/15",
        focused && "ring-2 ring-ui-accent ring-inset",
        dimmed && "opacity-50",
      )}
    >
      {picking ? (
        item.kind === "folder" ? (
          <span />
        ) : (
          <ListPickCheckbox
            selected={selected}
            disabled={unpickable != null && !selected}
            label={toggleLabel}
            onToggle={onToggle ?? (() => undefined)}
          />
        )
      ) : null}
      <ItemOpenButton
        item={item}
        focused={focused}
        ringed={false}
        onOpen={onOpen}
        onFocus={onFocus}
        title={unpickable ?? itemCaption(item)}
        className="flex min-w-0 items-center gap-3 rounded-md text-left"
      >
        <span className="relative shrink-0">
          <ItemMedia item={item} compact className="h-7 w-12 rounded" />
        </span>
        {/* Имя и хвост на одной базовой линии: text-sm и text-xs при items-center
            садятся каждый по центру своей строки, и «front» оказывается выше названия.
            Обрезка на внутреннем span: overflow у flex-элемента подменяет базовую линию
            нижним краем рамки. Имя отдаёт ширину раньше хвоста. */}
        <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
          <span className="min-w-0 shrink-[2]">
            <span className="block truncate text-sm text-ui-text-primary">{item.label}</span>
          </span>
          {item.current && currentLabel ? (
            <span className="inline-flex self-center">
              <CurrentVersionMark label={currentLabel} />
            </span>
          ) : null}
          {item.meta ? (
            <span className="max-w-[45%] min-w-0 shrink">
              <span className="block truncate text-xs text-ui-text-muted tabular-nums">{item.meta}</span>
            </span>
          ) : null}
        </span>
      </ItemOpenButton>
      <span className="flex min-w-0 items-center gap-1.5 text-xs text-ui-text-secondary">
        <KindIcon className="h-3 w-3 shrink-0 text-ui-text-muted" aria-hidden />
        <span className="truncate">{typeLabel}</span>
      </span>
      <span className={metricClass}>{duration ?? "—"}</span>
      <span className={metricClass}>{dateLabel}</span>
      {/* Заглушка обязательна: ROW_GRID объявляет последнюю колонку под кнопку действия. */}
      {actions ? <span className="flex gap-1 justify-self-end">{actions}</span> : <span />}
    </div>
  )
}
